import type { CombatAudioEngine, CombatPhase, PhaseInfo } from './CombatAudioEngine';

/**
 * The combo state machine of the approved audio-director reference, ported
 * without its DOM. It is the thing that decides *which* cue a kill is: the chain
 * count picks the phase (5 RUSH / 10 FEVER / 20 OVERDRIVE), the phase picks the
 * combo window (2s, 6s in fever), the window decides when the chain dies, and
 * every third kill is promoted from a small burst to a medium explosion so a
 * long chain keeps escalating instead of repeating.
 *
 * The reference's visual side (shake classes, flashes, the event log DOM) is
 * reduced to two listener hooks: `onEvent` for the log line and `onPhaseEnter`
 * for the phase-entry punch, so the harness can shake the screen on the same
 * beat the phase cue fires. The demo's auto-run driver is not ported — no key
 * binds it in the harness.
 */

export type CombatKillKind = 'normal' | 'mass' | 'elite' | 'bossPart' | 'bossCore';
export type CombatEventTone = '' | 'gold' | 'red';

export interface CombatEvent {
  readonly time: string;
  readonly label: string;
  readonly tone: CombatEventTone;
}

export interface CombatDirectorHooks {
  onEvent(label: string, tone: CombatEventTone): void;
  onPhaseEnter(phase: CombatPhase): void;
}

const NO_HOOKS: CombatDirectorHooks = { onEvent: () => undefined, onPhaseEnter: () => undefined };

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

const TICK_MS = 50;
const EVENT_LIMIT = 12;
const MAX_COMBO = 99;
const FEVER_LIMIT_MS = 20000;
const MS_PER_SECOND = 1000;

export class CombatDirector {
  combo = 0;
  score = 0;
  phase: CombatPhase = 'normal';
  private comboDeadline = 0;
  private feverDeadline = 0;
  private feverStartedAt = 0;
  private readonly events: CombatEvent[] = [];
  private readonly tickTimer: number;

  constructor(private readonly engine: CombatAudioEngine, private readonly hooks: CombatDirectorHooks = NO_HOOKS) {
    this.tickTimer = window.setInterval(() => this.tick(), TICK_MS);
  }

  phaseForCombo(c: number): CombatPhase {
    return c >= 20 ? 'overdrive' : c >= 10 ? 'fever' : c >= 5 ? 'rush' : 'normal';
  }

  windowForPhase(p: CombatPhase): number {
    return p === 'fever' ? 6000 : 2000;
  }

  multiplier(): number {
    if (this.phase === 'rush') return 2;
    if (this.phase === 'fever') return this.combo >= 15 ? 2.5 : this.combo >= 12 ? 2 : 1.5;
    if (this.phase === 'overdrive') return 3;
    return 1;
  }

  phaseInfo(): PhaseInfo {
    const phase = this.phase;
    let intensity = 0;
    if (phase === 'rush') intensity = clamp((this.combo - 5) / 4, 0, 1);
    if (phase === 'fever') intensity = clamp((this.combo - 10) / 9, 0, 1);
    if (phase === 'overdrive') intensity = clamp((this.combo - 20) / 6, 0, 1);
    return { phase, intensity };
  }

  /** Seconds left on the combo window — the harness's panel reads this. */
  get windowRemainingSeconds(): number {
    if (this.combo <= 0) return 0;
    return Math.max(0, (this.comboDeadline - performance.now()) / MS_PER_SECOND);
  }

  get lastEvent(): CombatEvent | null {
    return this.events[0] ?? null;
  }

  addEvent(label: string, tone: CombatEventTone = ''): void {
    const time = new Date().toLocaleTimeString('ja-JP', { hour12: false, minute: '2-digit', second: '2-digit' });
    this.events.unshift({ time, label, tone });
    this.events.length = Math.min(this.events.length, EVENT_LIMIT);
    this.hooks.onEvent(label, tone);
  }

  enterPhase(next: CombatPhase): void {
    if (next === this.phase) return;
    this.phase = next;
    this.engine.setPhase(next);
    if (next === 'rush') {
      this.engine.rushStart();
      this.addEvent('RUSH START · SUCTION → IMPACT → SPEED CADENCE', 'gold');
    }
    if (next === 'fever') {
      this.feverStartedAt = performance.now();
      this.feverDeadline = this.feverStartedAt + FEVER_LIMIT_MS;
      this.engine.feverStart();
      this.addEvent('FEVER START · MUSIC VACUUM → FANFARE → BLOOM', 'gold');
    }
    if (next === 'overdrive') {
      this.feverDeadline = 0;
      this.engine.overdriveStart();
      this.addEvent('OVERDRIVE IGNITION · CHARGE → SUB BLAST → CROWN', 'gold');
    }
    if (next === 'normal') {
      this.feverDeadline = 0;
      this.engine.setPhase('normal');
    }
    this.hooks.onPhaseEnter(next);
  }

  kill(amount = 1, kind: CombatKillKind = 'normal'): void {
    const now = performance.now();
    const previous = this.combo;
    const previousPhase = this.phase;
    this.combo = Math.min(MAX_COMBO, this.combo + amount);
    const nextPhase = this.phaseForCombo(this.combo);
    // enterPhase early-returns on an unchanged phase, so the phase is rewound
    // before the call — without this the entry cue would never fire.
    this.phase = previousPhase;
    if (nextPhase !== previousPhase) this.enterPhase(nextPhase);
    else this.engine.setPhase(nextPhase);
    this.phase = nextPhase;
    this.comboDeadline = now + this.windowForPhase(this.phase);
    let base = 100;
    if (this.phase === 'fever') base = 220;
    if (this.phase === 'overdrive') base = 300;
    if (kind === 'mass') base = 2500;
    if (kind === 'elite') base = 4000;
    if (kind === 'bossPart') base = 5000;
    if (kind === 'bossCore') base = 10000;
    const gained = Math.round(base * this.multiplier());
    this.score += gained;
    if (kind === 'bossCore') {
      this.engine.overkillExplosion();
      this.addEvent(`OVERKILL CORE · +${gained}`, 'gold');
    } else if (kind === 'bossPart') {
      this.engine.bossPartBreak();
      this.addEvent(`BOSS PART BREAK · +${gained}`, 'gold');
    } else if (kind === 'elite') {
      this.engine.mediumExplosion(this.combo);
      this.addEvent(`MEDIUM DESTROY · +${gained}`, 'gold');
    } else if (kind === 'mass') {
      this.engine.chainBurst(this.combo);
      this.addEvent(`CHAIN BURST · ${previous}→${this.combo} CHAIN`, 'gold');
    } else {
      if (this.combo % 3 === 0) this.engine.mediumExplosion(this.combo);
      else this.engine.normalKill(this.combo);
      this.addEvent(`ENEMY KILL · ${this.combo} CHAIN`);
    }
    if (nextPhase === previousPhase) {
      const chainDelay = kind === 'bossCore' ? .48 : kind === 'bossPart' ? .31 : kind === 'elite' ? .25 : kind === 'mass' ? .19 : .035;
      this.engine.chain(this.combo, chainDelay);
    }
    if (previous < 12 && this.combo >= 12) {
      this.engine.scorePop(3000, .44);
      this.score += Math.round(3000 * this.multiplier());
      this.addEvent('12 CHAIN BONUS · +3000 / GOLD CADENCE', 'gold');
    }
    if (previous < 15 && this.combo >= 15) {
      this.engine.scorePop(5000, .58);
      this.score += Math.round(5000 * this.multiplier());
      this.addEvent('15 CHAIN BONUS · +5000 / OCTAVE BLOOM', 'gold');
    }
    if (previous < 20 && this.combo >= 20) {
      this.engine.scorePop(1000, .9);
      this.score += Math.round(1000 * this.multiplier());
      this.addEvent('20 CHAIN BURST · +1000', 'gold');
    }
    if (previous < 22 && this.combo >= 22 && kind !== 'bossPart') {
      this.engine.scorePop(5000, .45);
      this.score += Math.round(5000 * this.multiplier());
      this.addEvent('22 CHAIN BURST · +5000', 'gold');
    }
    if (previous < 24 && this.combo >= 24 && kind !== 'bossCore') {
      this.engine.scorePop(10000, .65);
      this.score += Math.round(10000 * this.multiplier());
      this.addEvent('24 CHAIN BURST · +10000 / FINAL RESOLVE', 'gold');
    }
  }

  breakCombo(reason = 'COMBO BREAK'): void {
    if (this.combo === 0 && this.phase === 'normal') return;
    this.engine.comboBreak();
    this.combo = 0;
    this.comboDeadline = 0;
    this.feverDeadline = 0;
    this.phase = 'normal';
    this.engine.setPhase('normal');
    this.addEvent(reason, 'red');
  }

  reset(silent = false): void {
    this.combo = 0;
    this.score = 0;
    this.comboDeadline = 0;
    this.feverDeadline = 0;
    this.phase = 'normal';
    this.engine.setPhase('normal');
    if (!silent) this.addEvent('SYSTEM RESET');
  }

  tick(): void {
    const now = performance.now();
    if (this.combo > 0 && now >= this.comboDeadline) this.breakCombo('COMBO TIMER EXPIRED');
    else if (this.phase === 'fever' && this.feverDeadline && now >= this.feverDeadline) this.breakCombo('FEVER TIME UP');
  }

  dispose(): void {
    window.clearInterval(this.tickTimer);
  }
}
